import React from 'react';
import { Box, Button, Typography, Alert, AlertTitle } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Shown above the error. Defaults to a generic message. */
  title?: string;
  /** Render nothing at all on error instead of the fallback card. Use for
   *  non-essential overlays where a visible error would be more disruptive
   *  than silence. */
  silent?: boolean;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render/lifecycle errors in a subtree and shows a recoverable card
 * instead of unmounting the whole app.
 *
 * Without this, a single bad render anywhere under it takes the entire page to
 * a blank white screen with no way back — which is exactly what happened when a
 * task decision object arrived without its `reason` field and `.trim()` threw
 * inside the blocking daily gate.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
  }

  handleReset = () => this.setState({ error: null });

  handleReload = () => window.location.reload();

  render() {
    const { error } = this.state;
    const { children, title, silent } = this.props;

    if (!error) return children;
    if (silent) return null;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 2, maxWidth: 640, width: '100%' }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
            >
              Reload
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 700 }}>
            {title || 'Something went wrong'}
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This part of the page failed to load. Reloading usually fixes it.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {error.message}
          </Typography>
        </Alert>
      </Box>
    );
  }
}

export default ErrorBoundary;
